import { getLocalTimeZone, now, parseAbsoluteToLocal } from "@internationalized/date";
import { format as formatDateFns } from "date-fns/esm";
import { formatInTimeZone } from "date-fns-tz";
import { isThisYear } from "date-fns/esm";

import useTimeSettingsStore from "hooks/useTimeSettingsStore";

import { FormatDateProps, GetNowDateTimeProps } from "./types";

export const INVALID_DATE = "Invalid date";

/** `format` throws a RangeError on an invalid date — this returns INVALID_DATE instead. */
export const formatDateWithPattern = (date: Date | number, pattern: string): string => {
  try {
    return formatDateFns(date, pattern);
  } catch {
    return INVALID_DATE;
  }
};

export const isUserUsing12HourFormat = (): boolean => {
  const format = new Intl.DateTimeFormat(navigator.language, { hour: "numeric" });
  const parts = format.formatToParts(new Date());
  return parts.some((part) => part.type === "dayPeriod"); // e.g., "AM"/"PM"
};

const isCurrentYear = (date: Date | number, timezone?: string): boolean => {
  if (!timezone) {
    return isThisYear(date);
  }

  return (
    formatInTimeZone(date, timezone, "yyyy") === formatInTimeZone(new Date(), timezone, "yyyy")
  );
};

const getDatePattern = (date: Date | number, timezone?: string): string => {
  const yearPattern = isCurrentYear(date, timezone) ? "" : ", yyyy";

  return `MMM do${yearPattern}`;
};

const getDateTimePattern = (date: Date | number, timezone?: string): string => {
  const getResolvedTimeFormat = useTimeSettingsStore.getState().getResolvedTimeFormat;
  const is12Hour = getResolvedTimeFormat() === "12";

  const timePattern = is12Hour ? "h:mm:ss a" : "H:mm:ss";
  const pattern = `${getDatePattern(date, timezone)}, ${timePattern}`;

  return timezone ? `${pattern} zzz` : pattern;
};

/**
 * Formats a date according to the application's standard format pattern.
 *
 * @examples
 * Default format with time (uses local format):
 * formatDate({ date: new Date('2024-06-05T14:30:00') })
 *  → "Jun 5th, 2:30:00 PM" (12-hour format)
 *  → "Jun 5th, 14:30:00" (24-hour format, based on user locale)
 *
 * Status date format:
 * formatDate({ date: new Date('2024-06-05T14:30:00'), format: 'statusDate' })
 *  → "June 5, 2024"
 *
 * formatDate({ date: new Date('2022-12-25T18:45:00'), format: 'statusDate' })
 *  → "December 25, 2022"
 *
 * Date only format (no time, no timezone suffix), the year is omitted for the current year:
 * formatDate({ date: new Date('2024-06-05T14:30:00'), format: 'dateOnly' })
 *  → "Jun 5th" (current year)
 *
 * formatDate({ date: new Date('2022-12-25T18:45:00'), format: 'dateOnly' })
 *  → "Dec 25th, 2022"
 *
 * Current year date with timezone:
 * formatDate({ date: new Date('2024-06-05T14:30:00Z'), timezone: 'America/New_York' })
 *  → "Jun 5th, 10:30:00 AM EDT"
 *
 * formatDate({ date: new Date('2024-06-05T14:30:00Z'), timezone: 'Europe/London' })
 *  → "Jun 5th, 3:30:00 PM BST" or "Jun 5th, 3:30:00 PM GMT+1"
 *
 * formatDate({ date: new Date('2024-06-05T14:30:00Z'), timezone: 'UTC' })
 *  → "Jun 5th, 2:30:00 PM UTC"
 *
 * Past year date (includes year):
 * formatDate({ date: new Date('2022-12-25T18:45:00') })
 *  → "Dec 25th, 2022, 6:45:00 PM"
 *
 * Winter date with timezone (shows different timezone abbreviations):
 * formatDate({ date: new Date('2024-01-15T20:00:00Z'), timezone: 'America/New_York' })
 *  → "Jan 15th, 3:00:00 PM EST" (Eastern Standard Time in winter)
 *
 * Edge cases:
 * formatDate({ date: new Date('2024-02-29T12:00:00Z'), timezone: 'Asia/Tokyo' })
 *  → "Feb 29th, 9:00:00 PM JST" (leap year date)
 */
export const formatDate = ({ date, timezone, format = "default" }: FormatDateProps): string => {
  try {
    if (format === "statusDate") {
      return formatDateFns(date, "MMMM d, yyyy");
    }

    const pattern =
      format === "dateOnly" ? getDatePattern(date, timezone) : getDateTimePattern(date, timezone);

    return timezone ? formatInTimeZone(date, timezone, pattern) : formatDateFns(date, pattern);
  } catch {
    return INVALID_DATE;
  }
};

export const getNowDateTime = ({
  timeShiftMinutes = 0,
  timezone = getLocalTimeZone(),
}: GetNowDateTimeProps = {}): Date => {
  return parseAbsoluteToLocal(
    now(timezone).add({ minutes: timeShiftMinutes }).toAbsoluteString()
  ).toDate();
};
